import { Injectable, computed, signal } from '@angular/core';
import type { CompanyColumnDef } from '../interfaces/grid-column.types';
import type { GridSelectionMode } from '../interfaces/grid-config.types';
import { DEFAULT_GRID_CONFIG } from '../interfaces/grid-config.types';
import { defaultRowKey, filterRows, paginateRows, sortRows, type GridSortState } from './grid-state.utils';

export type GridRowKeyFn<T> = (row: T, index: number) => unknown;

/**
 * Cérebro reativo do grid, instanciado no escopo de DI local do Host.
 * Toda leitura de estado é feita via signal/computed — nenhuma mutação de dado
 * bruto acontece fora dos métodos públicos desta classe.
 */
@Injectable()
export class GridStateStore<T = Record<string, unknown>> {
  private readonly _rowData = signal<readonly T[]>([]);
  private readonly _loading = signal(false);
  private readonly _columns = signal<readonly CompanyColumnDef<T>[]>([]);
  private readonly _hiddenFields = signal<ReadonlySet<string>>(new Set());

  private readonly _globalFilterTerm = signal('');
  private readonly _columnFilters = signal<Readonly<Record<string, string>>>({});
  private readonly _sort = signal<GridSortState | null>(null);

  private readonly _currentPage = signal(1);
  private readonly _pageSize = signal(DEFAULT_GRID_CONFIG.pageSize);

  private readonly _selectionMode = signal<GridSelectionMode>(DEFAULT_GRID_CONFIG.selectionMode);
  private readonly _selectedRowKeys = signal<ReadonlySet<unknown>>(new Set());
  private _rowKeyFn: GridRowKeyFn<T> = defaultRowKey;

  readonly rowData = this._rowData.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly columns = this._columns.asReadonly();
  readonly globalFilterTerm = this._globalFilterTerm.asReadonly();
  readonly columnFilters = this._columnFilters.asReadonly();
  readonly sort = this._sort.asReadonly();
  readonly currentPage = this._currentPage.asReadonly();
  readonly pageSize = this._pageSize.asReadonly();
  readonly selectionMode = this._selectionMode.asReadonly();

  readonly visibleColumns = computed(() =>
    this._columns().filter(column => !this._hiddenFields().has(column.field))
  );

  /**
   * Dataset filtrado (busca global + filtros de coluna), ainda sem ordenação/paginação.
   * É o que o bridge do AG Grid consome como `rowData` — sort e paginação nativos do
   * AG Grid assumem a partir daqui (ver adaptação de arquitetura no relatório da POC).
   * `sortedData`/`pagedData` continuam disponíveis para a Facade e para bridges futuros
   * que não tenham motor de sort/paginação próprio.
   */
  readonly filteredData = computed(() =>
    filterRows(this._rowData(), this._columns(), this._globalFilterTerm(), this._columnFilters())
  );

  private readonly sortedData = computed(() => sortRows(this.filteredData(), this._sort()));

  readonly totalRecords = computed(() => this.sortedData().length);

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalRecords() / this._pageSize())));

  readonly pagedData = computed(() =>
    paginateRows(this.sortedData(), this._currentPage(), this._pageSize())
  );

  readonly selectedRowKeys = this._selectedRowKeys.asReadonly();

  readonly selectedData = computed(() => {
    const keys = this._selectedRowKeys();
    if (keys.size === 0) return [];
    return this._rowData().filter((row, index) => keys.has(this._rowKeyFn(row, index)));
  });

  setRowKeyFn(fn: GridRowKeyFn<T>): void {
    this._rowKeyFn = fn;
  }

  rowKeyOf(row: T, index: number): unknown {
    return this._rowKeyFn(row, index);
  }

  setRowData(rows: readonly T[]): void {
    this._rowData.set(rows);
  }

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  registerColumn(column: CompanyColumnDef<T>): void {
    const withoutDuplicate = this._columns().filter(existing => existing.field !== column.field);
    this._columns.set([...withoutDuplicate, column]);
  }

  setColumns(columns: readonly CompanyColumnDef<T>[]): void {
    const current = this._columns();
    if (current.length === columns.length && columns.every((column, index) => column === current[index])) {
      return;
    }
    this._columns.set(columns);
  }

  unregisterColumn(field: string): void {
    this._columns.set(this._columns().filter(column => column.field !== field));
  }

  setColumnVisible(field: string, visible: boolean): void {
    const next = new Set(this._hiddenFields());
    visible ? next.delete(field) : next.add(field);
    this._hiddenFields.set(next);
  }

  setGlobalFilter(term: string): void {
    this._globalFilterTerm.set(term);
    this._currentPage.set(1);
  }

  setColumnFilter(field: string, term: string): void {
    this._columnFilters.set({ ...this._columnFilters(), [field]: term });
    this._currentPage.set(1);
  }

  clearColumnFilter(field: string): void {
    const { [field]: _removed, ...rest } = this._columnFilters();
    this._columnFilters.set(rest);
  }

  setSort(sort: GridSortState | null): void {
    this._sort.set(sort);
  }

  setPage(page: number): void {
    this._currentPage.set(Math.min(Math.max(1, page), this.totalPages()));
  }

  setPageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._currentPage.set(1);
  }

  setSelectionMode(mode: GridSelectionMode): void {
    this._selectionMode.set(mode);
    if (mode === 'none') this._selectedRowKeys.set(new Set());
  }

  toggleRowSelection(row: T, index: number): void {
    const key = this._rowKeyFn(row, index);
    const next = new Set(this._selectedRowKeys());

    if (next.has(key)) {
      next.delete(key);
    } else {
      if (this._selectionMode() === 'single') next.clear();
      next.add(key);
    }

    this._selectedRowKeys.set(next);
  }

  setSelectedRows(rows: readonly T[]): void {
    const keys = new Set(rows.map((row, index) => this._rowKeyFn(row, index)));
    const current = this._selectedRowKeys();
    if (keys.size === current.size && [...keys].every(key => current.has(key))) {
      return;
    }
    this._selectedRowKeys.set(keys);
  }

  clearSelection(): void {
    this._selectedRowKeys.set(new Set());
  }

  updateCellValue(rowIndex: number, field: string, value: unknown): T | null {
    const rows = this._rowData();
    const target = rows[rowIndex];
    if (!target) return null;

    const updatedRow = { ...(target as Record<string, unknown>), [field]: value } as T;
    const nextRows = rows.slice();
    nextRows[rowIndex] = updatedRow;
    this._rowData.set(nextRows);
    return updatedRow;
  }
}
